import { criterion, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { hatWasserzeichen, readHeaderOoxml } from "@/office/word/einfuegen";
import { MAKRO_SEITENFARBE } from "../makros";

/** RGB-Wert in VBA: Weiß ist 16777215 (&HFFFFFF). */
const WEISS = 16777215;

export const lektionWasserzeichen: Lesson = {
  id: "wasserzeichen",
  title: "Wasserzeichen, Seitenfarbe und Seitenrand",
  summary: "Die drei Werkzeuge der Gruppe „Seitenhintergrund“ – und wann sie angebracht sind.",
  durationMinutes: 8,
  learningGoals: [
    "Ein Wasserzeichen wie „ENTWURF“ oder „VERTRAULICH“ einfügen",
    "Seitenfarbe und Seitenrand setzen und ihre Druckwirkung kennen",
  ],
  steps: [
    {
      id: "wasserzeichen",
      title: "Ein Wasserzeichen",
      explanation: [
        "Ein Wasserzeichen ist blasser Text oder ein Bild hinter dem Inhalt jeder Seite. " +
          "„Entwurf → Wasserzeichen“ bietet fertige Varianten wie „ENTWURF“, " +
          "„VERTRAULICH“ oder „MUSTER“; „Benutzerdefiniertes Wasserzeichen“ erlaubt eigenen " +
          "Text oder ein Logo.",
        "Technisch liegt das Wasserzeichen in der Kopfzeile – deshalb erscheint es auf jeder " +
          "Seite, und deshalb lässt es sich im Fließtext nicht versehentlich markieren oder " +
          "verschieben. Wer es entfernen will, nutzt „Wasserzeichen entfernen“ im selben Menü.",
        "Ein Wasserzeichen „ENTWURF“ ist die ehrlichste Art zu sagen, dass ein Dokument noch " +
          "nicht fertig ist. Es übersteht Ausdrucke und PDF-Export – ein Hinweis im " +
          "Dateinamen tut das nicht.",
      ],
      keyPoints: [
        "Entwurf → Wasserzeichen.",
        "Das Wasserzeichen liegt in der Kopfzeile und erscheint auf jeder Seite.",
        "„ENTWURF“ übersteht Druck und PDF, ein Dateiname nicht.",
      ],
      task: {
        instruction: "Füge deinem Dokument das Wasserzeichen „ENTWURF“ oder ein eigenes Textwasserzeichen hinzu.",
        hint: "Entwurf → Wasserzeichen → „ENTWURF 1“ oder „Benutzerdefiniertes Wasserzeichen“.",
        check: async () => {
          const vorhanden = hatWasserzeichen(await readHeaderOoxml());
          return fromCriteria([criterion("Ein Wasserzeichen ist eingefügt", vorhanden)], {
            success: "Das Wasserzeichen steht auf jeder Seite.",
            failure: "Es wurde noch kein Wasserzeichen gefunden.",
            hint: "Ein Textfeld mit hellgrauem Text ist kein Wasserzeichen – nutze Entwurf → Wasserzeichen.",
          });
        },
      },
    },
    {
      id: "seitenfarbe-und-rand",
      title: "Seitenfarbe und Seitenrand",
      explanation: [
        "„Entwurf“ → „Seitenfarbe“ färbt den Hintergrund aller Seiten. Das ist für " +
          "Bildschirmdokumente gedacht: Word druckt die Seitenfarbe nur, wenn unter " +
          "„Datei“ → „Optionen“ → „Anzeige“ „Hintergrundfarben und -bilder drucken“ " +
          "aktiviert ist.",
        "„Entwurf“ → „Seitenränder“ – nicht zu verwechseln mit den Seitenrändern unter " +
          "„Layout“ – zieht einen Rahmen um die ganze Seite. Urkunden und Einladungen nutzen " +
          "das; für Berichte ist er fast immer zu viel.",
        "Beide Einstellungen sind dokumentweit und gehören damit, wenn überhaupt, in eine " +
          "Vorlage für einen bestimmten Zweck.",
      ],
      keyPoints: [
        "Entwurf → Seitenfarbe – druckt nur mit eingeschalteter Option.",
        "Entwurf → Seitenränder = Rahmen um die Seite.",
      ],
      task: {
        instruction: "Gib deinem Dokument eine helle Seitenfarbe und einen einfachen Seitenrahmen.",
        hint: "Entwurf → Seitenfarbe → hellen Ton wählen. Entwurf → Seitenränder → „Kontur“.",
        macro: MAKRO_SEITENFARBE,
        check: () =>
          pruefeMitMakro(MAKRO_SEITENFARBE, (bericht) => {
            const farbe = bericht.zahl("hintergrundFarbe");
            const gesetzt = bericht.janein("hintergrund") === true && farbe !== null && farbe !== WEISS;
            return fromCriteria(
              [
                criterion("Eine Seitenfarbe ist gesetzt", gesetzt),
                criterion("Ein Seitenrahmen ist gesetzt", bericht.janein("seitenrahmen") === true),
              ],
              {
                success: "Farbe und Rahmen stehen – für den Druck ist die Farbe ausdrücklich einzuschalten.",
                failure: "Seitenfarbe oder Seitenrahmen fehlen noch.",
                hint: "Weiß zählt nicht als Seitenfarbe. Beim Rahmen die Einstellung „Kontur“ wählen.",
              },
            );
          }),
      },
    },
  ],
};
